import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { showingbookerAPI } from '../../../services/showingbookerAPI';

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 20px;
  z-index: 1000;
  overflow-y: auto;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 8px;
  max-width: 1200px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  margin-top: 20px;
`;

const Header = styled.div`
  padding: 20px;
  border-bottom: 1px solid #eee;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 0;
  background: white;
  z-index: 10;
`;

const Title = styled.h2`
  margin: 0;
  color: #333;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #999;
  
  &:hover {
    color: #333;
  }
`;

const Content = styled.div`
  padding: 20px;
`;

const LogTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
`;

const Th = styled.th`
  background: #f5f5f5;
  padding: 12px 8px;
  text-align: left;
  font-weight: bold;
  border-bottom: 2px solid #ddd;
  position: sticky;
  top: 60px;
  background: #f5f5f5;
  white-space: nowrap;
`;

const Td = styled.td`
  padding: 12px 8px;
  border-bottom: 1px solid #eee;
  vertical-align: top;
`;

const NotificationBadge = styled.span`
  display: inline-block;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: bold;
  margin: 2px;
  background: ${props => props.sent ? '#d4edda' : '#f8d7da'};
  color: ${props => props.sent ? '#155724' : '#721c24'};
`;

const ExpandButton = styled.button`
  background: #007AFF;
  color: white;
  border: none;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  
  &:hover {
    background: #0056b3;
  }
`;

const DetailRow = styled.tr`
  background: #f9f9f9;
`;

const DetailCell = styled.td`
  padding: 16px;
  border-bottom: 2px solid #ddd;
`;

const ExportButton = styled.button`
  padding: 8px 16px;
  background: #28a745;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  margin-left: 10px;
  
  &:hover {
    background: #218838;
  }
`;

const PropertyInfo = styled.div`
  background: #f8f9fa;
  padding: 15px;
  border-radius: 4px;
  margin-bottom: 20px;
`;

const PropertyLogs = ({ property, onClose }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedRow, setExpandedRow] = useState(null);

  useEffect(() => {
    loadLogs();
  }, [property.id]);

  const loadLogs = async () => {
    try {
      // Try to load from backend API (endpoint needs to be implemented)
      const data = await showingbookerAPI.getPropertyBookings(property.id);
      setLogs(data || []);
    } catch (error) {
      console.error('Error loading logs:', error);
      // Fallback to localStorage for testing
      const localLogs = JSON.parse(localStorage.getItem('showingbooker_logs') || '[]');
      const filtered = localLogs.filter(log => log.propertyId === property.id);
      setLogs(filtered);
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = () => {
    const headers = [
      'Booking Date',
      'Showing Date',
      'Showing Time',
      'Confirmation #',
      'Buyer Name',
      'Buyer Email',
      'Buyer Phone',
      'Buyer Agent',
      'Agent Phone',
      'Resident Name',
      'Resident Phone',
      'Payment Amount',
      'Payment ID',
      'Mail Upgrade',
      'SMS to Agent',
      'SMS to Resident',
      'SMS to Buyer',
      'Email to Agent',
      'Email to Resident', 
      'Email to Buyer',
      'Certified Mail',
      'Tracking #',
      'Special Requests'
    ];

    const rows = logs.map(log => [
      new Date(log.bookingCreatedAt).toLocaleString(),
      log.showingDate,
      log.showingTime,
      log.confirmationNumber,
      log.buyerName,
      log.buyerEmail,
      log.buyerPhone,
      log.buyerAgentName,
      log.buyerAgentPhone,
      log.residentInfo?.name || 'N/A',
      log.residentInfo?.phone || 'N/A',
      `$${log.payment.amount}`,
      log.payment.stripePaymentIntentId,
      log.payment.includedMailUpgrade ? 'Yes' : 'No',
      log.notificationDetails.sms.listing_agent.status,
      log.notificationDetails.sms.resident.status,
      log.notificationDetails.sms.buyer.status,
      log.notificationDetails.email.listing_agent.status,
      log.notificationDetails.email.resident.status,
      log.notificationDetails.email.buyer.status,
      log.notificationDetails.certifiedMail.status,
      log.notificationDetails.certifiedMail.trackingNumber,
      log.specialRequests
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `property-logs-${property.id}-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return 'N/A';
    const date = new Date(isoString);
    return date.toLocaleString();
  };

  const toggleExpand = (index) => {
    setExpandedRow(expandedRow === index ? null : index);
  };

  return (
    <Modal onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <Header>
          <Title>📋 Compliance Logs - {property.address_line1}</Title>
          <div>
            <ExportButton onClick={exportToCSV}>
              📥 Export CSV
            </ExportButton>
            <CloseButton onClick={onClose}>×</CloseButton>
          </div>
        </Header>
        
        <Content>
          <PropertyInfo>
            <strong>Property:</strong> {property.address_line1}
            {property.unit_number && `, ${property.unit_number}`}, {property.city}, {property.state} {property.zip_code}<br />
            <strong>Status:</strong> {property.is_occupied ? 'Occupied' : 'Vacant'}<br />
            <strong>Total Showings:</strong> {logs.length}
          </PropertyInfo>

          {loading ? (
            <div>Loading logs...</div>
          ) : logs.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
              No showings have been booked for this property yet.
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <LogTable>
                <thead>
                  <tr>
                    <Th>Booking Date</Th>
                    <Th>Showing Date/Time</Th>
                    <Th>Confirmation</Th>
                    <Th>Buyer Info</Th>
                    <Th>Resident</Th>
                    <Th>Payment</Th>
                    <Th>Notifications</Th>
                    <Th>Details</Th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log, index) => (
                    <React.Fragment key={index}>
                      <tr>
                        <Td>
                          {new Date(log.bookingCreatedAt).toLocaleDateString()}<br />
                          <small>{new Date(log.bookingCreatedAt).toLocaleTimeString()}</small>
                        </Td>
                        <Td>
                          <strong>{log.showingDate}</strong><br />
                          {log.showingTime}
                        </Td>
                        <Td>
                          <strong>{log.confirmationNumber}</strong>
                        </Td>
                        <Td>
                          <strong>{log.buyerName}</strong><br />
                          <small>{log.buyerEmail}</small><br />
                          <small>{log.buyerPhone}</small>
                          {log.buyerAgentName !== 'Not provided' && (
                            <>
                              <br />
                              <small>Agent: {log.buyerAgentName}</small>
                            </>
                          )}
                        </Td>
                        <Td>
                          {log.residentInfo ? (
                            <>
                              {log.residentInfo.name}<br />
                              <small>{log.residentInfo.phone}</small>
                            </>
                          ) : (
                            <span style={{ color: '#999' }}>Not occupied</span>
                          )}
                        </Td>
                        <Td>
                          <strong>${log.payment.amount}</strong><br />
                          <small style={{ color: '#28a745' }}>✓ Paid</small>
                          {log.payment.includedMailUpgrade && (
                            <>
                              <br />
                              <small>📬 Mail upgrade</small>
                            </>
                          )}
                        </Td>
                        <Td>
                          <div>
                            <NotificationBadge sent={log.notificationDetails.sms.listing_agent.status === 'Sent'}>
                              SMS Agent
                            </NotificationBadge>
                            <NotificationBadge sent={log.notificationDetails.sms.buyer.status === 'Sent'}>
                              SMS Buyer
                            </NotificationBadge>
                            {log.residentInfo && (
                              <NotificationBadge sent={log.notificationDetails.sms.resident.status === 'Sent'}>
                                SMS Resident
                              </NotificationBadge>
                            )}
                          </div>
                          <div style={{ marginTop: '4px' }}>
                            <NotificationBadge sent={log.notificationDetails.email.listing_agent.status === 'Sent'}>
                              Email Agent
                            </NotificationBadge>
                            <NotificationBadge sent={log.notificationDetails.email.buyer.status === 'Sent'}>
                              Email Buyer
                            </NotificationBadge>
                            {log.residentInfo && (
                              <NotificationBadge sent={log.notificationDetails.email.resident.status === 'Sent'}>
                                Email Resident
                              </NotificationBadge>
                            )}
                          </div>
                          {log.payment.includedMailUpgrade && (
                            <div style={{ marginTop: '4px' }}>
                              <NotificationBadge sent={log.notificationDetails.certifiedMail.status.includes('Sent')}>
                                Certified Mail
                              </NotificationBadge>
                            </div>
                          )}
                        </Td>
                        <Td>
                          <ExpandButton onClick={() => toggleExpand(index)}>
                            {expandedRow === index ? 'Hide' : 'View'} Details
                          </ExpandButton>
                        </Td>
                      </tr>
                      {expandedRow === index && (
                        <DetailRow>
                          <DetailCell colSpan="8">
                            <div style={{ display: 'grid', gap: '20px', gridTemplateColumns: '1fr 1fr' }}>
                              <div>
                                <h4>📱 SMS Notifications</h4>
                                <ul style={{ listStyle: 'none', padding: 0 }}>
                                  <li>
                                    <strong>Listing Agent:</strong> {log.notificationDetails.sms.listing_agent.status}
                                    {log.notificationDetails.sms.listing_agent.sentAt && (
                                      <> at {formatDateTime(log.notificationDetails.sms.listing_agent.sentAt)}</>
                                    )}
                                    <br />
                                    <small>Phone: {log.notificationDetails.sms.listing_agent.phone}</small>
                                  </li>
                                  <li style={{ marginTop: '8px' }}>
                                    <strong>Buyer:</strong> {log.notificationDetails.sms.buyer.status}
                                    {log.notificationDetails.sms.buyer.sentAt && (
                                      <> at {formatDateTime(log.notificationDetails.sms.buyer.sentAt)}</>
                                    )}
                                    <br />
                                    <small>Phone: {log.notificationDetails.sms.buyer.phone}</small>
                                  </li>
                                  {log.residentInfo && (
                                    <li style={{ marginTop: '8px' }}>
                                      <strong>Resident:</strong> {log.notificationDetails.sms.resident.status}
                                      {log.notificationDetails.sms.resident.sentAt && (
                                        <> at {formatDateTime(log.notificationDetails.sms.resident.sentAt)}</>
                                      )}
                                      <br />
                                      <small>Phone: {log.notificationDetails.sms.resident.phone}</small>
                                    </li>
                                  )}
                                </ul>

                                <h4 style={{ marginTop: '20px' }}>📧 Email Notifications</h4>
                                <ul style={{ listStyle: 'none', padding: 0 }}>
                                  <li>
                                    <strong>Listing Agent:</strong> {log.notificationDetails.email.listing_agent.status}
                                    {log.notificationDetails.email.listing_agent.sentAt && (
                                      <> at {formatDateTime(log.notificationDetails.email.listing_agent.sentAt)}</>
                                    )}
                                    <br />
                                    <small>Email: {log.notificationDetails.email.listing_agent.email}</small>
                                  </li>
                                  <li style={{ marginTop: '8px' }}>
                                    <strong>Buyer:</strong> {log.notificationDetails.email.buyer.status}
                                    {log.notificationDetails.email.buyer.sentAt && (
                                      <> at {formatDateTime(log.notificationDetails.email.buyer.sentAt)}</>
                                    )}
                                    <br />
                                    <small>Email: {log.notificationDetails.email.buyer.email}</small>
                                  </li>
                                  {log.residentInfo && (
                                    <li style={{ marginTop: '8px' }}>
                                      <strong>Resident:</strong> {log.notificationDetails.email.resident.status}
                                      {log.notificationDetails.email.resident.sentAt && (
                                        <> at {formatDateTime(log.notificationDetails.email.resident.sentAt)}</>
                                      )}
                                      <br />
                                      <small>Email: {log.notificationDetails.email.resident.email}</small>
                                    </li>
                                  )}
                                </ul>
                              </div>

                              <div>
                                <h4>💳 Payment Details</h4>
                                <ul style={{ listStyle: 'none', padding: 0 }}>
                                  <li><strong>Amount:</strong> ${log.payment.amount}</li>
                                  <li><strong>Status:</strong> {log.payment.status}</li>
                                  <li><strong>Stripe ID:</strong> <small>{log.payment.stripePaymentIntentId}</small></li>
                                  <li><strong>Processed:</strong> {formatDateTime(log.payment.processedAt)}</li>
                                  <li><strong>Mail Upgrade:</strong> {log.payment.includedMailUpgrade ? 'Yes' : 'No'}</li>
                                </ul>

                                {log.payment.includedMailUpgrade && (
                                  <>
                                    <h4 style={{ marginTop: '20px' }}>📬 Certified Mail</h4>
                                    <ul style={{ listStyle: 'none', padding: 0 }}>
                                      <li><strong>Status:</strong> {log.notificationDetails.certifiedMail.status}</li>
                                      {log.notificationDetails.certifiedMail.sentAt && (
                                        <li><strong>Sent:</strong> {formatDateTime(log.notificationDetails.certifiedMail.sentAt)}</li>
                                      )}
                                      <li><strong>Tracking:</strong> {log.notificationDetails.certifiedMail.trackingNumber}</li>
                                    </ul>
                                  </>
                                )}

                                {log.specialRequests !== 'None' && (
                                  <>
                                    <h4 style={{ marginTop: '20px' }}>📝 Special Requests</h4>
                                    <p>{log.specialRequests}</p>
                                  </>
                                )}
                              </div>
                            </div>
                          </DetailCell>
                        </DetailRow>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </LogTable>
            </div>
          )}
        </Content>
      </ModalContent>
    </Modal>
  );
};

export default PropertyLogs;